import {
  buildConceptComboKey,
  buildConceptStatsExceededMessage,
  buildConceptStatsRequest,
  fetchConceptStats,
  formatConceptCodeToLabel,
  isConceptStatsExceeded,
  parseConceptStatsData,
  type ConceptStatsData,
} from "../../../services/autoTaggingService";

/** Draft row fields used to identify a concept combination. */
export type ConceptComboDraftItem = {
  id: string;
  objectType: string;
  objectSubType: string;
  conceptCode: string;
};

/** Concept that has no remaining plan slots (or draft would exceed available). */
export type ConceptPlanLimitItem = {
  objectType: string;
  objectSubType: string;
  conceptCode: string;
  conceptLabel: string;
  currentCount: number;
  limit: number;
};

export class ConceptPlanLimitError extends Error {
  readonly items: ConceptPlanLimitItem[];

  constructor(items: ConceptPlanLimitItem[], message = "Plan limit reached.") {
    super(message);
    this.name = "ConceptPlanLimitError";
    this.items = items;
  }
}

export function isConceptPlanLimitError(
  error: unknown,
): error is ConceptPlanLimitError {
  return error instanceof ConceptPlanLimitError;
}

function toPlanLimitItem(
  selection: ConceptComboSelection,
  stats: ConceptStatsData,
): ConceptPlanLimitItem {
  const conceptCode = stats.concept || selection.conceptCode;
  return {
    objectType: selection.objectType,
    objectSubType: selection.objectSubType,
    conceptCode,
    conceptLabel: formatConceptCodeToLabel(conceptCode) || conceptCode,
    currentCount: stats.current_count,
    limit: stats.limit,
  };
}

function isStatsAtHardLimit(stats: ConceptStatsData): boolean {
  return !stats.can_create || stats.available <= 0;
}

function planLimitItemKey(item: ConceptPlanLimitItem): string {
  return [
    item.objectType.trim().toLowerCase(),
    item.objectSubType.trim().toLowerCase(),
    item.conceptCode.trim(),
  ].join("|");
}

export type ConceptComboSelection = {
  objectType: string;
  objectSubType: string;
  conceptCode: string;
};

export function resolveConceptComboSelection(
  item: Pick<ConceptComboDraftItem, "objectType" | "objectSubType" | "conceptCode">,
  defaultObjectType: string,
): ConceptComboSelection {
  return {
    objectType: item.objectType.trim() || defaultObjectType,
    objectSubType: item.objectSubType,
    conceptCode: item.conceptCode,
  };
}

/** Unique complete combos (all three dropdowns filled) in the draft list. */
export function collectUniqueConceptCombos(
  items: readonly ConceptComboDraftItem[],
  defaultObjectType: string,
): Map<string, ConceptComboSelection> {
  const combos = new Map<string, ConceptComboSelection>();

  items.forEach((item) => {
    const selection = resolveConceptComboSelection(item, defaultObjectType);
    const key = buildConceptComboKey(selection);
    if (!key) return;
    combos.set(key, selection);
  });

  return combos;
}

/** Group draft rows by concept combo key (only rows with a complete combo). */
export function groupDraftItemsByConceptCombo<T extends ConceptComboDraftItem>(
  items: readonly T[],
  defaultObjectType: string,
): Map<string, T[]> {
  const groups = new Map<string, T[]>();

  items.forEach((item) => {
    const selection = resolveConceptComboSelection(item, defaultObjectType);
    const key = buildConceptComboKey(selection);
    if (!key) return;

    const group = groups.get(key) ?? [];
    group.push(item);
    groups.set(key, group);
  });

  return groups;
}

export function areAllComboStatsLoaded(
  items: readonly ConceptComboDraftItem[],
  statsByComboKey: Readonly<Record<string, ConceptStatsData>>,
  defaultObjectType: string,
): boolean {
  const combos = collectUniqueConceptCombos(items, defaultObjectType);
  return [...combos.keys()].every((key) => Boolean(statsByComboKey[key]));
}

/**
 * Collect every concept combo whose draft count exceeds remaining plan slots.
 */
export function collectExceededConceptLimits(
  items: readonly ConceptComboDraftItem[],
  statsByComboKey: Readonly<Record<string, ConceptStatsData>>,
  defaultObjectType: string,
): ConceptPlanLimitItem[] {
  const groups = groupDraftItemsByConceptCombo(items, defaultObjectType);
  const exceeded: ConceptPlanLimitItem[] = [];

  for (const [key, groupItems] of groups) {
    const stats = statsByComboKey[key];
    if (!stats) continue;

    if (isConceptStatsExceeded(stats, groupItems.length)) {
      const selection = resolveConceptComboSelection(
        groupItems[0],
        defaultObjectType,
      );
      exceeded.push(toPlanLimitItem(selection, stats));
    }
  }

  return exceeded;
}

export function buildPlanLimitToastMessage(
  items: readonly ConceptPlanLimitItem[],
): string {
  const first = items[0];
  if (!first) return "Plan limit reached.";
  // When the max is reached, show limit/limit (fully used), not a stale current_count.
  return `The maximum of ${first.limit} plans for this combination has been reached (${first.limit}/${first.limit} used).`;
}

function planLimitItemMatchesDraft(
  item: ConceptComboDraftItem,
  seed: ConceptPlanLimitItem,
  defaultObjectType: string,
): boolean {
  const selection = resolveConceptComboSelection(item, defaultObjectType);
  return (
    selection.objectType.trim().toLowerCase() ===
      seed.objectType.trim().toLowerCase() &&
    selection.objectSubType.trim().toLowerCase() ===
      seed.objectSubType.trim().toLowerCase() &&
    selection.conceptCode.trim() === seed.conceptCode.trim()
  );
}

export type CollectPlanLimitClearItemIdsOptions = {
  /** When set, only clear this draft row (the one the user just changed). */
  onlyItemId?: string;
  /**
   * When provided, keep up to `available` rows per combo (draft order) and
   * clear only the overflow — used for async stats-loaded exceed handling.
   */
  availableByComboKey?: Readonly<Record<string, number>>;
};

/** Draft item ids whose concept should be cleared after a plan-limit hit. */
export function collectPlanLimitClearItemIds(
  items: readonly ConceptComboDraftItem[],
  seeds: readonly ConceptPlanLimitItem[],
  defaultObjectType: string,
  options?: CollectPlanLimitClearItemIdsOptions,
): string[] {
  if (seeds.length === 0) return [];

  if (options?.onlyItemId) {
    const target = items.find((item) => item.id === options.onlyItemId);
    if (!target) return [];
    const matches = seeds.some((seed) =>
      planLimitItemMatchesDraft(target, seed, defaultObjectType),
    );
    return matches ? [options.onlyItemId] : [];
  }

  if (options?.availableByComboKey) {
    const clearIds: string[] = [];
    const groups = groupDraftItemsByConceptCombo(items, defaultObjectType);

    for (const [key, groupItems] of groups) {
      const matchesSeed = groupItems.some((item) =>
        seeds.some((seed) =>
          planLimitItemMatchesDraft(item, seed, defaultObjectType),
        ),
      );
      if (!matchesSeed) continue;

      const available = options.availableByComboKey[key] ?? 0;
      const overflow =
        available <= 0 ? groupItems : groupItems.slice(Math.max(0, available));
      for (const item of overflow) {
        clearIds.push(item.id);
      }
    }

    return clearIds;
  }

  return items
    .filter((item) =>
      seeds.some((seed) =>
        planLimitItemMatchesDraft(item, seed, defaultObjectType),
      ),
    )
    .map((item) => item.id);
}

/**
 * For each exceeded Object Type + Sub-Type scope, fetch stats for all known
 * concepts and include every concept that has already reached its hard limit.
 * Matches the dialog design that lists all full concepts, not only the draft one.
 */
export async function expandExceededLimitsWithAtLimitSiblings(
  seedItems: readonly ConceptPlanLimitItem[],
  conceptCodes: readonly string[],
): Promise<ConceptPlanLimitItem[]> {
  if (seedItems.length === 0) return [];

  const merged = new Map<string, ConceptPlanLimitItem>();
  for (const item of seedItems) {
    merged.set(planLimitItemKey(item), item);
  }

  const uniqueCodes = [
    ...new Set(conceptCodes.map((code) => code.trim()).filter(Boolean)),
  ];
  if (uniqueCodes.length === 0) {
    return [...merged.values()];
  }

  const scopes = new Map<string, { objectType: string; objectSubType: string }>();
  for (const item of seedItems) {
    const scopeKey = `${item.objectType.trim().toLowerCase()}|${item.objectSubType.trim().toLowerCase()}`;
    if (!item.objectType.trim()) continue;
    scopes.set(scopeKey, {
      objectType: item.objectType,
      objectSubType: item.objectSubType,
    });
  }

  await Promise.all(
    [...scopes.values()].map(async (scope) => {
      const results = await Promise.all(
        uniqueCodes.map(async (conceptCode) => {
          const selection = {
            objectType: scope.objectType,
            objectSubType: scope.objectSubType,
            conceptCode,
          };
          const response = await fetchConceptStats(
            buildConceptStatsRequest(selection),
          );
          const stats = parseConceptStatsData(response);
          if (!stats || !isStatsAtHardLimit(stats)) return null;
          return toPlanLimitItem(selection, stats);
        }),
      );

      for (const item of results) {
        if (!item) continue;
        merged.set(planLimitItemKey(item), item);
      }
    }),
  );

  const ordered = [...merged.values()];
  ordered.sort((left, right) => {
    const leftIndex = uniqueCodes.indexOf(left.conceptCode);
    const rightIndex = uniqueCodes.indexOf(right.conceptCode);
    const safeLeft = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
    const safeRight = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;
    if (safeLeft !== safeRight) return safeLeft - safeRight;
    return left.conceptLabel.localeCompare(right.conceptLabel);
  });
  return ordered;
}

/**
 * Compare draft counts per combo against cached API stats.
 * Returns the first exceeded message, or null when within limits.
 */
export function validateDraftComboCounts(
  items: readonly ConceptComboDraftItem[],
  statsByComboKey: Readonly<Record<string, ConceptStatsData>>,
  defaultObjectType: string,
): string | null {
  const groups = groupDraftItemsByConceptCombo(items, defaultObjectType);

  for (const [key, groupItems] of groups) {
    const stats = statsByComboKey[key];
    if (!stats) continue;

    if (isConceptStatsExceeded(stats, groupItems.length)) {
      return buildConceptStatsExceededMessage(stats, groupItems.length);
    }
  }

  return null;
}

/** Fetch concept-stats for every unique combo and merge into the cache. */
export async function fetchConceptStatsForCombos(
  combos: Map<string, ConceptComboSelection>,
): Promise<Record<string, ConceptStatsData>> {
  const entries = await Promise.all(
    [...combos.entries()].map(async ([key, combo]) => {
      const response = await fetchConceptStats(buildConceptStatsRequest(combo));
      const stats = parseConceptStatsData(response);
      if (!stats) {
        const message =
          response.message?.trim() || "Failed to check plan availability.";
        throw new Error(message);
      }
      return [key, stats] as const;
    }),
  );

  return Object.fromEntries(entries);
}

/**
 * Validate draft rows against fresh concept-stats (used before import / submit).
 * Throws when any combo exceeds its remaining slot count.
 */
export async function assertDraftComboAvailability(
  items: readonly ConceptComboDraftItem[],
  defaultObjectType: string,
): Promise<void> {
  const combos = collectUniqueConceptCombos(items, defaultObjectType);
  if (combos.size === 0) return;

  const statsByComboKey = await fetchConceptStatsForCombos(combos);
  const exceeded = collectExceededConceptLimits(
    items,
    statsByComboKey,
    defaultObjectType,
  );
  if (exceeded.length > 0) {
    throw new ConceptPlanLimitError(
      exceeded,
      validateDraftComboCounts(items, statsByComboKey, defaultObjectType) ??
        "Plan limit reached.",
    );
  }
}

/**
 * Edit keeps the original Object Type + Sub-Type + Concept without consuming an
 * extra slot. Only switching to a different combo needs remaining capacity.
 */
export async function assertEditComboAvailability(
  formItem: ConceptComboDraftItem,
  originalItem: ConceptComboDraftItem,
  defaultObjectType: string,
): Promise<void> {
  const formSelection = resolveConceptComboSelection(
    formItem,
    defaultObjectType,
  );
  const originalSelection = resolveConceptComboSelection(
    originalItem,
    defaultObjectType,
  );
  const formKey = buildConceptComboKey(formSelection);
  const originalKey = buildConceptComboKey(originalSelection);

  if (!formKey) return;
  if (formKey === originalKey) return;

  await assertDraftComboAvailability([formItem], defaultObjectType);
}

export function validateImportedFormItems(
  items: ReadonlyArray<{
    objectType: string;
    objectSubType: string;
    conceptCode: string;
    conceptValues: ReadonlyArray<unknown>;
    allValues: boolean;
  }>,
  options?: {
    /** When true for a given object type, sub-type is required (e.g. Insight). RTQ has none. */
    requiresObjectSubType?: (objectType: string) => boolean;
  },
): string | null {
  if (items.length === 0) {
    return "No items found in the uploaded file.";
  }

  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    const objectType = item.objectType.trim();
    const requireObjectSubType =
      options?.requiresObjectSubType?.(objectType) ?? false;

    if (!objectType || !item.conceptCode.trim()) {
      return `Item ${index + 1} is missing object type or concept.`;
    }

    if (requireObjectSubType && !item.objectSubType.trim()) {
      return `Item ${index + 1} is missing object sub-type.`;
    }

    if (!item.allValues && item.conceptValues.length === 0) {
      return `Item ${index + 1} is missing concept values.`;
    }
  }

  return null;
}

export function isConceptComboSelectionPatch(
  patch: Partial<
    Pick<ConceptComboDraftItem, "objectType" | "objectSubType" | "conceptCode">
  >,
): boolean {
  return (
    patch.objectType !== undefined ||
    patch.objectSubType !== undefined ||
    patch.conceptCode !== undefined
  );
}
